import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import { Check, Copy, Play, Square, Eraser } from "lucide-react";
import { toast } from "sonner";
import { AssistantLogsService, type AssistantLogLine } from "../services/assistantLogsService";
import type { SSESubscription } from "@/shared/utils/fetchSSE";
import { LogTerminal, LogTerminalRef } from "@/shared/components/ui/log-terminal";
import { LogLine } from "@/shared/components/ui/log-line";

const HISTORICAL_TAIL = 2000;

// Cap on accumulated live lines so long sessions don't grow the buffer
// without bound (would inflate memory and slow re-renders since the list
// is rendered directly, without virtualization).
const MAX_LIVE_LINES = 2000;

const LIVE_OPTION_VALUE = "__live__";

const formatDateLabel = (fileName: string): string => {
  const match = fileName.match(/app-(\d{4})(\d{2})(\d{2})\.log/);
  if (!match) return fileName;
  return `${match[1]}-${match[2]}-${match[3]}`;
};

const todayLogFileName = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `app-${yyyy}${mm}${dd}.log`;
};

// Pure renderer for a single log line — no state/prop dependencies, so it
// lives outside the component to avoid being recreated on every render.
const renderLogLine = (log: AssistantLogLine) => (
  <LogLine timestamp={log.timestamp} level={log.level} message={log.message} />
);

export const AssistantLogs = () => {
  const { t } = useTranslation();
  const [isPaused, setIsPaused] = useState(false);
  const [logLevel, setLogLevel] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [logs, setLogs] = useState<AssistantLogLine[]>([]);
  const [copied, setCopied] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null); // null = live
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const logTerminalRef = useRef<LogTerminalRef>(null);
  const eventSourceRef = useRef<SSESubscription | null>(null);
  const isPausedRef = useRef(isPaused);

  const isLive = selectedFile === null;

  // Keep ref in sync with pause state — SSE callback reads ref to avoid stale closures
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Load list of available log files on mount
  useEffect(() => {
    AssistantLogsService.getLogs()
      .then((response) => {
        const files = response.logFiles ?? [];
        setAvailableFiles(files);
      })
      .catch(() => {
        toast.error(t("logs.failedToListFiles", "Could not load the list of available dates."));
      });
  }, [t]);

  const closeStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const openLiveStream = useCallback(() => {
    closeStream();
    // Initial tail = MAX_LIVE_LINES so the live view opens with the full day so
    // far (capped to the in-memory limit) instead of just the last 200 lines.
    // Without this, anyone opening "Live" mid-afternoon would lose visibility
    // of everything that happened earlier in the same day. Subsequent lines
    // arrive via SSE as usual.
    eventSourceRef.current = AssistantLogsService.createLogStream(
      (log) => {
        if (isPausedRef.current) return;
        setLogs((prev) => {
          const next = prev.length >= MAX_LIVE_LINES ? [...prev.slice(-(MAX_LIVE_LINES - 1)), log] : [...prev, log];
          return next;
        });
      },
      undefined,
      () => {
        toast.error(t("assistant.logs.connectionError"));
      },
      undefined,
      MAX_LIVE_LINES
    );
  }, [closeStream, t]);

  // Switch between live stream and historical snapshot when selection changes
  useEffect(() => {
    if (selectedFile === null) {
      setLogs([]);
      openLiveStream();
      return () => closeStream();
    }

    let cancelled = false;
    closeStream();
    setIsLoadingHistorical(true);
    setLogs([]);

    AssistantLogsService.getLogs(selectedFile, HISTORICAL_TAIL)
      .then((response) => {
        if (cancelled) return;
        const lines = (response.lines ?? []).map((line) => ({
          id: crypto.randomUUID(),
          timestamp: line.timestamp,
          level: line.level,
          message: line.message,
        }));
        setLogs(lines);
        if (lines.length === 0 && (!response.raw || response.raw.trim().length === 0)) {
          toast.warning(
            t("logs.historicalUnavailable", "Historical file unavailable — it may have been rotated out.")
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        toast.error(
          t("logs.historicalUnavailable", "Historical file unavailable — it may have been rotated out.")
        );
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingHistorical(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFile, openLiveStream, closeStream, t]);

  // Auto-scroll to the bottom when new lines arrive
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (logTerminalRef.current) {
        logTerminalRef.current.scrollToBottom();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [logs]);

  const togglePause = () => setIsPaused((prev) => !prev);

  const clearLogs = () => setLogs([]);

  // Today's file is covered by the "Live" option (which now opens with
  // MAX_LIVE_LINES of initial tail, so it carries the full day so far) —
  // drop it from the historical list to avoid duplication.
  const historicalOptions = useMemo(() => {
    const today = todayLogFileName();
    return availableFiles
      .filter((f) => f !== today)
      .map((f) => ({ value: f, label: formatDateLabel(f) }));
  }, [availableFiles]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      // Filtro por nível
      if (logLevel !== "all") {
        const levelUpper = log.level.toUpperCase();
        const filterUpper = logLevel.toUpperCase();

        const levelMatch =
          levelUpper === filterUpper ||
          (filterUpper === "INF" && levelUpper === "INFO") ||
          (filterUpper === "INFO" && levelUpper === "INF") ||
          (filterUpper === "WRN" && levelUpper === "WARN") ||
          (filterUpper === "WARN" && levelUpper === "WRN") ||
          (filterUpper === "ERR" && levelUpper === "ERROR") ||
          (filterUpper === "ERROR" && levelUpper === "ERR") ||
          (filterUpper === "DBG" && levelUpper === "DEBUG") ||
          (filterUpper === "DEBUG" && levelUpper === "DBG");

        if (!levelMatch) return false;
      }

      // Filtro por texto
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const messageMatch = log.message.toLowerCase().includes(query);
        const timestampMatch = log.timestamp.toLowerCase().includes(query);
        const levelMatch = log.level.toLowerCase().includes(query);

        if (!messageMatch && !timestampMatch && !levelMatch) return false;
      }

      return true;
    });
  }, [logs, logLevel, searchQuery]);

  const copyLogs = () => {
    const logText = filteredLogs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.message}`)
      .join("\n");

    navigator.clipboard
      .writeText(logText)
      .then(() => {
        setCopied(true);
        toast.success(t("logs.copied", "Logs copiados"));
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error(t("logs.copyError", "Erro ao copiar"));
      });
  };

  // Sem Card próprio: o container (AssistantLogsTab) fornece o Card, como nas
  // demais abas do painel de Logs (PBI 81485).
  return (
    <div className="w-full space-y-4">
        {/* Busca e Filtros */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              placeholder={t("assistant.logs.searchPlaceholder", "Buscar nos logs...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 max-w-md min-w-[180px]"
            />
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {t("logs.dateSelector", "Data")}:
              </span>
              <select
                value={selectedFile ?? LIVE_OPTION_VALUE}
                onChange={(e) =>
                  setSelectedFile(e.target.value === LIVE_OPTION_VALUE ? null : e.target.value)
                }
                className="h-9 text-sm px-3 rounded-md border border-input bg-background"
              >
                <option value={LIVE_OPTION_VALUE}>
                  {t("logs.liveOption", "Tempo real (hoje)")}
                </option>
                {historicalOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {t("assistant.logs.level", "Nível")}:
              </span>
              <select
                value={logLevel}
                onChange={(e) => setLogLevel(e.target.value)}
                className="h-9 text-sm px-3 rounded-md border border-input bg-background"
              >
                <option value="all">{t("assistant.logs.allLevels", "Todos os níveis")}</option>
                <option value="INF">INF</option>
                <option value="WRN">WRN</option>
                <option value="ERR">ERR</option>
                <option value="DBG">DBG</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm">
                {isLive ? (
                  isPaused ? (
                    <span className="flex items-center text-yellow-500">
                      <span className="h-2 w-2 rounded-full bg-yellow-500 mr-2"></span>
                      {t("logs.paused", "Pausado")}
                    </span>
                  ) : (
                    <span className="flex items-center text-green-500">
                      <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></span>
                      {t("logs.streamingActive", "Monitoramento em tempo real ativo")}
                    </span>
                  )
                ) : (
                  <span className="flex items-center text-blue-400">
                    <span className="h-2 w-2 rounded-full bg-blue-400 mr-2"></span>
                    {isLoadingHistorical
                      ? t("logs.loadingHistorical", "Carregando logs históricos...")
                      : t(
                          "logs.historicalSnapshot",
                          "Snapshot histórico — sem atualização em tempo real"
                        )}
                  </span>
                )}
              </span>
              {filteredLogs.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {filteredLogs.length} {t("assistant.logs.lines", "linhas")}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="h-[500px] overflow-hidden">
          {logs.length === 0 && !isLoadingHistorical ? (
            <div className="flex flex-col items-center justify-center h-full border rounded-md bg-card text-muted-foreground">
              <p>{t("logs.waitingForLogs", "Aguardando logs...")}</p>
            </div>
          ) : (
            <LogTerminal
              className="h-full"
              ref={logTerminalRef}
              title={t("logs.terminalTitle", "Terminal de Logs do Assistant")}
              actionButtons={
                <div className="flex space-x-3">
                  {isLive &&
                    (isPaused ? (
                      <button
                        onClick={togglePause}
                        className="text-green-500 hover:text-green-700 focus:outline-none"
                        title={t("logs.resume", "Retomar")}
                      >
                        <Play className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={togglePause}
                        className="text-yellow-500 hover:text-yellow-700 focus:outline-none"
                        title={t("logs.pause", "Pausar")}
                      >
                        <Square className="h-4 w-4" />
                      </button>
                    ))}
                  {isLive && (
                    <button
                      onClick={clearLogs}
                      className={`text-gray-500 hover:text-gray-700 focus:outline-none ${
                        logs.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      title={t("logs.clearLogs", "Limpar")}
                      disabled={logs.length === 0}
                    >
                      <Eraser className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={copyLogs}
                    className={`text-gray-500 hover:text-gray-700 focus:outline-none ${
                      filteredLogs.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    title={t("logs.copyLogsTitle", "Copiar Logs")}
                    disabled={filteredLogs.length === 0}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              }
            >
              {filteredLogs.map((log) => (
                <div key={log.id} className="py-0.5 whitespace-pre-wrap break-all">
                  {renderLogLine(log)}
                </div>
              ))}
              {isLive && !isPaused && (
                <div className="h-4 flex items-center mt-1">
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></div>
                  <span className="text-xs text-green-600">
                    {t("logs.liveStreaming", "Streaming ao vivo")}
                  </span>
                </div>
              )}
            </LogTerminal>
          )}
        </div>
    </div>
  );
};
